'use client';

import { cn } from '../utils/cn';

interface SwitchProps {
	checked: boolean;
	onChange: (checked: boolean) => void;
	size?: 'sm' | 'md';
	disabled?: boolean;
	/** Lets a `<label htmlFor>` name the switch (clicking the label toggles it). */
	id?: string;
	'aria-label'?: string;
	'aria-labelledby'?: string;
	/** Id of the text that explains the switch (its consequence line). */
	'aria-describedby'?: string;
	className?: string;
	theme?: 'neutral' | 'accent';
}

const sizeStyles = {
	sm: {
		track: 'h-5 w-9',
		thumb: 'h-4 w-4',
		translate: 'translate-x-4',
	},
	md: {
		track: 'h-6 w-10',
		thumb: 'h-4 w-4',
		translate: 'translate-x-4',
	},
};

export const Switch = ({
	checked,
	onChange,
	size = 'md',
	disabled = false,
	className,
	theme = 'neutral',
	id,
	...props
}: SwitchProps) => {
	const s = sizeStyles[size];

	return (
		<button
			type="button"
			id={id}
			role="switch"
			aria-checked={checked}
			aria-label={props['aria-label']}
			aria-labelledby={props['aria-labelledby']}
			aria-describedby={props['aria-describedby']}
			disabled={disabled}
			onClick={() => onChange(!checked)}
			className={cn(
				'relative inline-flex items-center shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors',
				'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
				'disabled:pointer-events-none disabled:opacity-50',
				checked ? (theme === 'accent' ? 'bg-accent' : 'bg-neutral') : 'bg-neutral/20',
				s.track,
				className,
			)}
		>
			<span
				className={cn(
					'pointer-events-none inline-block rounded-full bg-surface shadow-sm transition-transform',
					s.thumb,
					checked ? s.translate : 'translate-x-0',
				)}
			/>
		</button>
	);
};
